import React from 'react';
import { cn } from '@/lib/utils';
import { cva, type VariantProps } from 'class-variance-authority';

const badgeVariants = cva(
  'inline-flex items-center justify-center rounded-full font-medium transition-all duration-300 whitespace-nowrap',
  {
    variants: {
      variant: {
        success: 'bg-success/10 text-success border border-success/20',
        warning: 'bg-warning/10 text-warning border border-warning/20',
        error: 'bg-destructive/10 text-destructive border border-destructive/20',
        info: 'bg-info/10 text-info border border-info/20',
        primary: 'bg-primary/10 text-primary border border-primary/20',
        secondary: 'bg-muted text-muted-foreground border border-border',
        ghost: 'bg-transparent text-muted-foreground',
        gradient: 'bg-gradient-to-r from-primary to-orange-400 text-white border-0',
      },
      size: {
        xs: 'px-2 py-0.5 text-[10px]',
        sm: 'px-2.5 py-0.5 text-xs',
        md: 'px-3 py-1 text-xs',
        lg: 'px-4 py-1.5 text-sm',
      },
      glow: {
        true: 'shadow-lg',
        false: '',
      },
    },
    compoundVariants: [
      { variant: 'success', glow: true, class: 'shadow-success/30' },
      { variant: 'warning', glow: true, class: 'shadow-warning/30' },
      { variant: 'error', glow: true, class: 'shadow-destructive/30' },
      { variant: 'info', glow: true, class: 'shadow-info/30' },
      { variant: 'primary', glow: true, class: 'shadow-primary/30' },
      { variant: 'gradient', glow: true, class: 'shadow-primary/40' },
    ],
    defaultVariants: {
      variant: 'secondary',
      size: 'md',
      glow: false,
    },
  }
);

export interface CinematicBadgeProps extends VariantProps<typeof badgeVariants> {
  children: React.ReactNode;
  pulse?: boolean;
  className?: string;
  leftIcon?: React.ReactNode;
  rightIcon?: React.ReactNode;
}

export const CinematicBadge = React.forwardRef<HTMLSpanElement, CinematicBadgeProps>(
  ({ children, variant, size, glow, pulse, className, leftIcon, rightIcon }, ref) => {
    return (
      <span
        ref={ref}
        className={cn(
          badgeVariants({ variant, size, glow }),
          pulse && 'animate-pulse',
          'hover:scale-105 transition-transform',
          className
        )}
      >
        {leftIcon && <span className="mr-1.5 flex items-center">{leftIcon}</span>}
        {children}
        {rightIcon && <span className="ml-1.5 flex items-center">{rightIcon}</span>}
      </span>
    );
  }
);

CinematicBadge.displayName = 'CinematicBadge';

export default CinematicBadge;
